import React, { useEffect, useRef } from "react";
import { COMPANY } from "@/lib/content";

/**
 * Calendly inline widget — buyers book without leaving the site.
 *
 * Injects the official Calendly assets script once, then mounts an inline
 * booking iframe pointed at COMPANY.calendlyUrl. Uses IntersectionObserver so
 * the widget only loads once it scrolls into view — keeps LCP clean.
 */
export default function CalendlyInline({ url, minHeight = 720, testId = "calendly-inline" }) {
  const bookingUrl = url || COMPANY.calendlyUrl;
  const containerRef = useRef(null);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current || scriptLoadedRef.current) return;

    const load = () => {
      if (scriptLoadedRef.current) return;
      scriptLoadedRef.current = true;
      const existing = document.querySelector("script[data-calendly='1']");
      if (existing) return;
      const s = document.createElement("script");
      s.src = "https://assets.calendly.com/assets/external/widget.js";
      s.async = true;
      s.setAttribute("data-calendly", "1");
      document.body.appendChild(s);
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(link);
    };

    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        load();
        io.disconnect();
      }
    }, { rootMargin: "300px" });
    io.observe(containerRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className="calendly-inline-widget bg-white border border-[#E2E8F0]"
      data-url={`${bookingUrl}?hide_gdpr_banner=1&primary_color=1c3e5f&text_color=1c3e5f`}
      style={{ minWidth: 320, height: `${minHeight}px` }}
    />
  );
}
